/**
 * Universe Debug Panel
 * 
 * Hidden debug overlay for diagnosing gesture/render issues.
 * Enable via: localStorage.setItem('ul_debug_universe', '1')
 * 
 * Shows:
 * - Current gesture mode
 * - Scale + offset
 * - Node count
 * - FPS estimate
 * - Visibility state
 */

import React, { useEffect, useState, useRef } from 'react';
import { GESTURE_MODES } from './gestureState';

// Check if debug mode is enabled
const isDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('ul_debug_universe') === '1';
};

export function UniverseDebugPanel({ 
  gestureMode = GESTURE_MODES.IDLE,
  scale = 1,
  offset = { x: 0, y: 0 },
  nodeCount = 0,
  isVisible = true,
  hitDebug = null  // Tap-after-zoom debug info
}) {
  const [fps, setFps] = useState(0);
  const [enabled, setEnabled] = useState(() => isDebugEnabled()); // Initialize from localStorage
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(0); // Initialize with 0, set in effect
  
  // Listen for storage changes (enable/disable in real-time)
  useEffect(() => {
    const handleStorage = () => setEnabled(isDebugEnabled());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  
  // FPS counter
  useEffect(() => {
    if (!enabled) return;
    
    // Initialize time ref
    lastTimeRef.current = performance.now();
    let animationId;
    
    const measureFps = () => {
      frameCountRef.current++;
      const now = performance.now();
      const elapsed = now - lastTimeRef.current;
      
      if (elapsed >= 1000) {
        setFps(Math.round(frameCountRef.current * 1000 / elapsed));
        frameCountRef.current = 0;
        lastTimeRef.current = now;
      }
      
      animationId = requestAnimationFrame(measureFps);
    };
    
    animationId = requestAnimationFrame(measureFps);
    return () => cancelAnimationFrame(animationId);
  }, [enabled]);
  
  if (!enabled) return null;
  
  // Mode color coding
  const modeColors = {
    [GESTURE_MODES.IDLE]: '#6B7280',
    [GESTURE_MODES.PANNING]: '#10B981',
    [GESTURE_MODES.PINCHING]: '#F59E0B',
    [GESTURE_MODES.INERTIA]: '#8B5CF6'
  };
  
  return (
    <div 
      className="fixed top-16 left-4 bg-black/90 text-white text-xs font-mono p-3 rounded-lg border border-gray-700 space-y-1"
      style={{ zIndex: 999999, pointerEvents: 'none' }}
    >
      <div className="text-gray-400 mb-2">🔧 Universe Debug</div>
      
      <div className="flex items-center gap-2">
        <span className="text-gray-500">Mode:</span>
        <span 
          className="px-2 py-0.5 rounded text-black font-bold"
          style={{ backgroundColor: modeColors[gestureMode] || '#6B7280' }}
        >
          {gestureMode}
        </span>
      </div>
      
      <div>
        <span className="text-gray-500">Scale:</span>{' '}
        <span className="text-green-400">{(scale * 100).toFixed(0)}%</span>
      </div>
      
      <div>
        <span className="text-gray-500">Offset:</span>{' '}
        <span className="text-blue-400">
          ({offset.x.toFixed(0)}, {offset.y.toFixed(0)})
        </span>
      </div>
      
      <div>
        <span className="text-gray-500">Nodes:</span>{' '}
        <span className="text-yellow-400">{nodeCount}</span>
      </div>
      
      <div>
        <span className="text-gray-500">FPS:</span>{' '}
        <span className={fps < 20 ? 'text-red-400' : fps < 30 ? 'text-yellow-400' : 'text-green-400'}>
          {fps}
        </span>
      </div>
      
      <div>
        <span className="text-gray-500">Visible:</span>{' '}
        <span className={isVisible ? 'text-green-400' : 'text-red-400'}>
          {isVisible ? 'yes' : 'no'}
        </span>
      </div>
      
      {/* Tap-after-zoom debug info */}
      {hitDebug ? (
        <div className="mt-2 pt-2 border-t border-gray-700">
          <div className="text-gray-400 mb-1">🎯 Last Tap</div>
          <div className={hitDebug.type === 'hit' ? 'text-green-400' : 'text-red-400'}>
            {hitDebug.type.toUpperCase()}: {hitDebug.name}
          </div>
          <div className="text-gray-300 text-[10px]">
            {typeof hitDebug.dist === 'number' ? `d=${hitDebug.dist.toFixed(4)}` : ''}
            {typeof hitDebug.thr === 'number' ? ` • t=${hitDebug.thr.toFixed(4)}` : ''}
            {typeof hitDebug.scale === 'number' ? ` • s=${hitDebug.scale.toFixed(2)}` : ''}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default UniverseDebugPanel;
